import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AVATARS, HitAvatar, avatarBus, nextFx, toAvatarSlug } from "@/components/avatar/HitAvatar";
import { RANK_META, RANK_ORDER, frameOf, useRankData, RankSummary, RankHowBody, AvatarStates } from "@/components/avatar/RankPanel";
import { Lock } from "lucide-react";

const COPY = {
  primera: { eyebrow: "NUEVO EN HIT TCG", sub: "Así te verán los demás en las subastas, en tus pujas y en tu perfil de comprador o vendedor." },
  registro: { eyebrow: "REGISTRO · PASO 3 DE 3", sub: "Te asignamos uno al azar. Cámbialo si quieres; puedes hacerlo cuando quieras desde tu perfil." },
  cambiar: { eyebrow: "TU PERFIL", sub: "Tu avatar aparece en el encabezado, en tus pujas y en tu perfil público." },
};
const FOCUSABLE = "button:not([disabled]), [href], input, [tabindex]:not([tabindex='-1'])";

const AvatarOption = ({ a, checked, tabbable, onPick }) => {
  const [hot, setHot] = useState(false);
  return (
    <button type="button" role="radio" aria-checked={checked} aria-label={a.label} tabIndex={tabbable ? 0 : -1} data-av={a.id}
      data-testid={`avatar-option-${a.id}`} onClick={() => onPick(a.id)}
      onMouseEnter={() => setHot(true)} onMouseLeave={() => setHot(false)} onFocus={() => setHot(true)} onBlur={() => setHot(false)}
      className={`relative flex flex-col items-center gap-2 pt-3.5 pb-2.5 px-1.5 rounded-[18px] border-2 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgba(255,255,255,.35)] transition-[border-color,background-color] duration-200 ${checked ? "border-[#FF4438] bg-[rgba(255,68,56,.1)]" : hot ? "border-white/10 bg-white/[.03]" : "border-transparent"}`}>
      <HitAvatar avatarId={a.id} name={a.label} size={64} lite={!(hot || checked)} />
      <span className={`text-xs font-semibold text-center leading-tight ${checked ? "text-white" : "text-white/70"}`}>{a.label}</span>
      <span aria-hidden className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-[#FF4438] text-[#0b0b0b] text-xs font-black grid place-items-center transition-opacity" style={{ opacity: checked ? 1 : 0 }}>✓</span>
    </button>
  );
};

const FramePreview = ({ avatarId, name, rank, size, frame, onPick }) => {
  const cur = RANK_ORDER.findIndex(([k]) => k === rank);
  return (
    <div className="mx-6 mb-5 p-4 bg-[#0b0b0b] border border-[#2a2a2a] rounded-[18px]" data-testid="avatar-frame-preview">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <span className="sx-mono text-[10px] tracking-[.14em] text-white/55">MARCOS · TOCA UNO GANADO PARA USARLO</span>
        <span className="text-xs text-white/55">+1 pt por cada compra o venta entregada</span>
      </div>
      <ol className="mt-3 grid grid-cols-5 gap-2">
        {RANK_ORDER.map(([k, min], i) => {
          const m = RANK_META[k], got = i <= cur, on = frame === k;
          return (
            <li key={k}>
              <button type="button" disabled={!got} onClick={() => onPick(k)} aria-pressed={on} data-testid={`frame-preview-${k}`}
                className={`w-full flex flex-col items-center gap-1.5 text-center py-2 rounded-[14px] border-2 transition-[border-color,background-color] ${on ? "bg-white/[.04]" : "border-transparent"} ${got ? "cursor-pointer hover:bg-white/[.03]" : "cursor-not-allowed"}`}
                style={on ? { borderColor: m.color } : undefined}>
                <HitAvatar avatarId={avatarId} name={name} size={size} rank={k} lite still />
                <span className="text-[11px] font-bold leading-tight" style={{ color: m.color }}>{m.label}</span>
                <span className="sx-mono text-[9px] text-white/60 whitespace-nowrap">{on ? "EN USO" : got ? "✓ GANADO" : <span className="inline-flex items-center gap-1"><Lock size={9} />{min} PTS</span>}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

const HowTab = ({ avatarId }) => {
  const data = useRankData();
  const { user } = useAuth();
  return (
    <div className="px-6 py-[22px] space-y-6" data-testid="avatar-modal-how">
      <RankSummary data={data} rank={data?.rank || user?.rank || "common"} />
      <RankHowBody data={data} />
      <AvatarStates avatarId={avatarId} />
    </div>
  );
};

export const AvatarPickerModal = ({ mode, onClose }) => {
  const { user, refresh } = useAuth();
  const [sel, setSel] = useState(() => mode === "registro" ? AVATARS[Math.floor(Math.random() * AVATARS.length)].id : mode === "cambiar" ? toAvatarSlug(user?.avatar_id) : null);
  const [fx, setFx] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const dlgRef = useRef(null);
  const [isDesktop] = useState(() => window.matchMedia("(min-width: 768px)").matches);
  const gridRef = useRef(null);
  const name = user?.name || "Tú";
  const copy = COPY[mode] || COPY.cambiar;
  const [tab, setTab] = useState("avatar");
  const [frame, setFrame] = useState(() => frameOf(user));
  const pickFrame = async (k) => {
    const prev = frame;
    setFrame(k);
    try { await api.patch("/users/me/frame", { frame: k }); await refresh(); toast.success(`Marco ${RANK_META[k].label} aplicado`); }
    catch { setFrame(prev); toast.error("No pudimos cambiar el marco."); }
  };

  useEffect(() => {
    dlgRef.current?.querySelector("#av-title")?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (e.key !== "Tab" || !dlgRef.current) return;
      const els = [...dlgRef.current.querySelectorAll(FOCUSABLE)].filter((el) => el.tabIndex !== -1);
      if (!els.length) return;
      const first = els[0], last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const gridKey = (e) => {
    const cols = 4;
    const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: cols, ArrowUp: -cols }[e.key];
    if (!step) return;
    e.preventDefault();
    const idx = Math.max(0, AVATARS.findIndex((a) => a.id === sel));
    const nxt = AVATARS[Math.min(AVATARS.length - 1, Math.max(0, (sel ? idx : -1) + step))] || AVATARS[0];
    setSel(nxt.id);
    requestAnimationFrame(() => gridRef.current?.querySelector(`[data-av="${nxt.id}"]`)?.focus({ preventScroll: true }));
  };

  const confirm = async () => {
    if (!sel || busy) return;
    setBusy(true);
    try {
      await api.patch("/users/me/avatar", { avatar_id: sel });
      setDone(true);
      setFx(nextFx("notify"));
      setTimeout(async () => { await refresh(); onClose(); avatarBus.notify(); }, 1050);
    } catch {
      toast.error("No pudimos guardar tu avatar. Intenta de nuevo.");
      setBusy(false);
    }
  };

  const dismissForever = async () => {
    try { await api.patch("/users/me/avatar-prompt", { dismissed: true }); await refresh(); } catch { /* el modal se cierra igual */ }
    onClose();
  };

  return createPortal(
    <div className="sx fixed inset-0 z-[90] bg-black/70 backdrop-blur-[8px] flex items-center justify-center p-4 overflow-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="avatar-modal-backdrop">
      <div ref={dlgRef} role="dialog" aria-modal="true" aria-labelledby="av-title" data-testid="avatar-modal" data-mode={mode}
        className="w-full max-w-[940px] max-h-[calc(100vh-32px)] overflow-auto bg-[#121212] border border-[#2a2a2a] rounded-[28px] flex flex-col">
        <div className="flex justify-between items-start gap-4 px-6 pt-6">
          <div className="flex flex-col gap-2">
            <span className="sx-mono text-[10px] tracking-[.14em] text-[#ff8a80]" data-testid="avatar-modal-eyebrow">{copy.eyebrow}</span>
            <h2 id="av-title" tabIndex={-1} className="outline-none m-0 text-[clamp(26px,4vw,36px)] font-extrabold tracking-[-.045em] leading-none">Elige tu avatar <span className="font-normal italic text-white/60">de subastador</span></h2>
            <span className="text-sm leading-relaxed text-white/60">{copy.sub}</span>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" data-testid="avatar-modal-x" className="w-[38px] h-[38px] flex-none rounded-full border border-[#2a2a2a] text-white/75 hover:text-white hover:bg-white/5 transition-colors">✕</button>
        </div>
        <div className="mx-6 mt-4 inline-flex self-start p-1 rounded-full bg-[#0b0b0b] border border-[#2a2a2a]" role="tablist" data-testid="avatar-modal-tabs">
          {[["avatar", "Elegir avatar"], ["how", "Cómo funciona"]].map(([k, t]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`avatar-tab-${k}`}
              className={`h-9 px-4 rounded-full text-sm font-bold transition-colors ${tab === k ? "bg-white text-[#0b0b0b]" : "text-white/70 hover:text-white"}`}>{t}</button>
          ))}
        </div>
        {tab === "how" ? <HowTab avatarId={sel || user?.avatar_id} /> : <>
        <div className="md:hidden mx-6 mt-4 flex items-center gap-3 p-3 bg-[#0b0b0b] border border-[#2a2a2a] rounded-[18px]" data-testid="avatar-preview-compact">
          <HitAvatar avatarId={sel} name={name} size={64} fx={fx} rank={frame} />
          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            <span className="text-base font-extrabold tracking-[-.02em] truncate">{name}</span>
            <div className="h-12 flex items-center gap-2 px-2.5 rounded-[12px] bg-[rgba(52,211,153,.07)] border border-[rgba(52,211,153,.35)]">
              <HitAvatar avatarId={sel} name={name} size={28} lite rank={frame} />
              <span className="flex-1 text-xs font-bold truncate">{name}</span><span className="text-sm font-extrabold text-[#34D399]">$24.00</span>
            </div>
          </div>
        </div>
        <div className="grid gap-5 px-6 py-[22px] md:grid-cols-[280px_auto] md:justify-between">
          <div className="hidden md:flex flex-col items-center gap-3.5 pt-[26px] pb-[18px] px-4 bg-[#0b0b0b] border border-[#2a2a2a] rounded-[22px] self-start" data-testid="avatar-preview">
            <div className="mt-2.5"><HitAvatar avatarId={sel} name={name} size={140} fx={isDesktop ? fx : undefined} rank={frame} /></div>
            <div className="flex flex-col items-center gap-0.5"><span className="text-[22px] font-extrabold tracking-[-.03em]">{name}</span></div>
            <div className="w-full flex flex-col gap-2 mt-1.5">
              <span className="sx-mono text-[9px] tracking-[.14em] text-white/45">ASÍ TE VERÁN EN UNA PUJA</span>
              <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-[14px] bg-[rgba(52,211,153,.07)] border border-[rgba(52,211,153,.35)]">
                <HitAvatar avatarId={sel} name={name} size={32} lite rank={frame} />
                <span className="flex-1 text-sm font-bold truncate">{name}</span><span className="text-base font-extrabold text-[#34D399]">$24.00</span>
              </div>
            </div>
          </div>
          <div ref={gridRef} role="radiogroup" aria-label="Avatares" onKeyDown={gridKey} className="grid grid-cols-4 md:grid-cols-[repeat(4,132px)] gap-2 md:gap-[14px] content-start" data-testid="avatar-grid">
            {AVATARS.map((a, i) => <AvatarOption key={a.id} a={a} checked={sel === a.id} tabbable={sel ? sel === a.id : i === 0} onPick={setSel} />)}
          </div>
        </div>
        <FramePreview avatarId={sel || AVATARS[0].id} name={name} rank={user?.rank || "common"} size={isDesktop ? 72 : 46} frame={frame} onPick={pickFrame} />
        </>}
        <div className="sticky bottom-0 z-[1] bg-[#121212] flex flex-col-reverse md:flex-row md:justify-between md:items-center gap-3 px-6 pt-4 pb-[22px] border-t border-[#2a2a2a]" data-testid="avatar-modal-footer">
          <div className="flex items-center justify-center md:justify-start gap-1.5 flex-wrap">
            <button type="button" onClick={onClose} data-testid="avatar-modal-close" className="h-11 px-4 rounded-full border border-[#2a2a2a] text-white/80 text-sm font-semibold hover:bg-white/5 transition-colors">
              {mode === "registro" ? "Elegir después" : mode === "cambiar" ? "Cancelar" : "Cerrar"}
            </button>
            {mode === "primera" && (
              <button type="button" onClick={dismissForever} data-testid="avatar-modal-dismiss" className="h-11 px-4 rounded-full text-white/55 text-sm font-semibold hover:text-white transition-colors">No volver a mostrar</button>
            )}
          </div>
          {tab === "avatar" && <button type="button" onClick={confirm} disabled={!sel || busy} data-testid="avatar-modal-confirm"
            className={`w-full md:w-auto h-[52px] px-7 rounded-full text-[#0b0b0b] text-[15px] font-extrabold transition-colors ${sel ? "bg-[#FF4438] hover:bg-[#ff5a4f]" : "bg-white/20 cursor-not-allowed"}`}>
            {done ? "¡Listo!" : "Confirmar avatar"}
          </button>}
        </div>
      </div>
    </div>,
    document.body,
  );
};

const BLOCKED = /^\/(checkout|pago|payment|mock-pay|subastas\/[^/]+|publish|publicar|cart|carrito|register|login|verify)/i;
const SESSION_KEY = "hittcg:avatar-prompt-shown";
const OPEN_KEY = "hittcg:avatar-modal-open";

// Monta el selector: primera vez (1×/sesión), registro y "cambiar" vía avatarBus.openPicker.
// El modo abierto se guarda en sessionStorage para sobrevivir un remontaje del árbol (p. ej. al refrescar la sesión).
export const AvatarPromptGate = ({ path }) => {
  const { user } = useAuth();
  const [mode, setModeRaw] = useState(() => sessionStorage.getItem(OPEN_KEY));
  const setMode = (m) => { if (m) sessionStorage.setItem(OPEN_KEY, m); else sessionStorage.removeItem(OPEN_KEY); setModeRaw(m); };

  useEffect(() => {
    const h = (e) => setMode(e.detail || "cambiar");
    window.addEventListener("hit-avatar:open-picker", h);
    return () => window.removeEventListener("hit-avatar:open-picker", h);
  }, []);

  useEffect(() => {
    if (!user || user.avatar_id || user.avatar_prompt_dismissed || mode) { return undefined; }
    if (sessionStorage.getItem(SESSION_KEY) || BLOCKED.test(path || "")) { return undefined; }
    let t;
    const attempt = (ms) => {
      t = setTimeout(() => {
        // Nunca encima de otra ventana: reintenta cuando se cierre.
        if (document.querySelector('[role="dialog"], [role="alertdialog"]')) { attempt(2000); return; }
        sessionStorage.setItem(SESSION_KEY, "1");
        setMode("primera");
      }, ms);
    };
    attempt(1500);
    return () => clearTimeout(t);
  }, [user, path, mode]);

  if (!mode || !user) return null;
  return <AvatarPickerModal mode={mode} onClose={() => setMode(null)} />;
};
